import React, { useEffect } from 'react';
import { Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './WhatToExpect.css';

const WhatToExpect = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('revealed');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const els = document.querySelectorAll('.scroll-reveal');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  const steps = [
    {
      title: 'Initial Forms',
      body: "You'll receive some initial forms. These help me understand you or your child before we meet, so our first session can focus on what matters most to you."
    },
    {
      title: 'Intake Session',
      body: "A full hour with parents and caregivers, or with you if the assessment is for you. This is a deep dive into everything I'd like to know, and everything you think I should know. For children, we usually do this without your child present, so they don't have to hear us talking about them in front of them."
    },
    {
      title: 'Gathering Information',
      body: "Alongside intake, I gather as much collateral information as possible: school reports (especially from primary school), doctors' letters, and any prior paediatrician, occupational therapy or speech pathology reports. If you can start collecting these early and email them through, that helps enormously."
    },
    {
      title: 'Assessment Days',
      body: "Unhurried time together across at least two sessions, on two different days, because nobody meets the world the same way every day. Depending on the assessments we decide on together, sessions are paced to suit you or your child."
    },
    {
      title: 'Feedback Session',
      body: "Once the assessment is complete, we book a full hour together to walk through every result in plain language, with time for every question. Your input matters here too: you know yourself, or your child, better than anyone."
    },
    {
      title: 'Report',
      body: "After the feedback session, I take in anything you've added, finalise everything, and send it to you: clear findings, practical recommendations for home, school or work, and a personal profile written just for the person who was assessed."
    },
    {
      title: 'Beyond the Report',
      body: "A report is not a goodbye. If you'd like ongoing support afterwards, I'm glad to keep working together. If you don't, that is completely fine too."
    }
  ];

  return (
    <div className="page-expect" data-testid="what-to-expect-page">
      <SEO
        title="What to Expect | Assessment Journey | Clinical Minds Psychology"
        description="Step-by-step guide to the assessment process at Clinical Minds Psychology in Coffs Harbour. From initial forms through to your report and ongoing support."
        canonicalUrl="https://www.clinicalminds.net/what-to-expect"
      />
      <ScrollIndicator />

      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Assessment</span>
          <h1 className="heading-hero">What to Expect</h1>
          <p className="hero-subline">The assessment journey, step by step</p>
        </div>
      </section>

      <section className="expect-intro scroll-reveal">
        <div className="container expect-narrow">
          <p className="body-large" style={{ textAlign: 'center', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            Starting the assessment process can feel daunting, so here is the journey, step by step. One thing stays constant the whole way through: you're with me from the first conversation to the final report. Nothing is outsourced.
          </p>
        </div>
      </section>

      <section className="expect-timeline-section scroll-reveal" data-testid="expect-timeline">
        <div className="container expect-narrow">
          <div className="expect-timeline">
            {steps.map((step, i) => (
              <div key={i} className={`expect-step scroll-reveal ${i === steps.length - 1 ? 'expect-step-final' : ''}`} data-testid={`expect-step-${i}`}>
                <div className="expect-step-number">{i + 1}</div>
                <div className="expect-step-content">
                  <h2 className="heading-3">{step.title}</h2>
                  <p className="body-medium">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="expect-duration-section scroll-reveal" data-testid="expect-duration">
        <div className="container expect-narrow">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '1.25rem' }}>How long does it all take?</h2>
          <p className="body-medium" style={{ textAlign: 'center', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            From the moment you decide to go ahead, intake is usually underway within about a month. Face-to-face assessment sessions typically happen around the two to three month mark, and you'll usually have the full report and personal profile in your hands around four to five months from saying yes. Every situation is a little different, so I'll confirm your dates when we speak, and you'll always know where things are up to.
          </p>
        </div>
      </section>

      <section className="expect-cta-section" data-testid="expect-cta">
        <div className="container">
          <div className="expect-cta-card">
            <h2 className="heading-2">Ready to get started?</h2>
            <p className="body-large">Send us an enquiry or give us a call. We're happy to answer your questions.</p>
            <div className="expect-cta-buttons">
              <Link to="/contact" className="btn-primary" data-testid="expect-enquiry-btn">
                Make an Enquiry
              </Link>
              <a href="tel:0266002710" className="expect-phone-cta">
                <Phone size={18} /> 02 6600 2710
              </a>
            </div>
          </div>
        </div>
      </section>

      <CrisisSupport />
    </div>
  );
};

export default WhatToExpect;
